import type { UseMutationResult } from "@tanstack/react-query";
import { bytes, shortHash } from "../lib/format";
import type { DocumentDetail as Detail } from "../lib/types";
import { Button, Chip, Panel, STATUS_TONE } from "./ui";

export function DocumentHeader({
  document: document_,
  hasClaims,
  runId,
  reparse,
  onExportWorkbook,
}: {
  document: Detail;
  hasClaims: boolean;
  runId: string | null;
  reparse: UseMutationResult<unknown, unknown, void>;
  onExportWorkbook: () => void;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
        <div className="min-w-0 flex-1">
          <h2
            title={document_.filename ?? undefined}
            className="truncate text-[1.35rem] leading-none tracking-tighter uppercase"
          >
            {document_.filename ?? "unnamed document"}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 font-mono text-[10.5px] text-faint">
            <Chip tone={STATUS_TONE[document_.status] ?? "mut"} dot>
              {document_.status}
            </Chip>
            <span>sha256 {shortHash(document_.sha256)}</span>
            <span className="tnum">{bytes(document_.byte_size)}</span>
            {document_.content_type && <span>{document_.content_type}</span>}
            {document_.carrier && <span className="text-dim">{document_.carrier}</span>}
            <span>parser {document_.parser_version}</span>
          </div>
        </div>
        {(document_.status === "failed" || document_.status === "unknown") && (
          <Button disabled={reparse.isPending} onClick={() => reparse.mutate()}>
            {reparse.isPending ? "queueing…" : "re-parse"}
          </Button>
        )}
        {hasClaims && runId && (
          <Button variant="primary" onClick={onExportWorkbook}>
            export workbook
          </Button>
        )}
      </div>

      {document_.status === "stale" && (
        <Panel title="parsed by an older build">
          <div className="flex flex-col gap-3 px-4 py-4">
            <p className="max-w-[74ch] text-[13px] leading-relaxed text-dim">
              This document was read successfully, but by an earlier parser version. This
              server runs{" "}
              <span className="font-mono text-gold">{document_.parser_version}</span>, and
              nothing at that version exists for it yet — so its pages, fields and findings
              cannot be shown. Reading it again fixes that; parsing is idempotent, so it
              costs one pass and nothing else changes.
            </p>
            <Button
              variant="primary"
              disabled={reparse.isPending}
              onClick={() => reparse.mutate()}
              className="self-start"
            >
              {reparse.isPending ? "queueing…" : `re-parse at ${document_.parser_version}`}
            </Button>
            {reparse.isError && (
              <p className="font-mono text-[11.5px] text-crit">
                &gt; {reparse.error instanceof Error ? reparse.error.message : "could not queue"}
              </p>
            )}
            {reparse.isSuccess && (
              <p className="font-mono text-[11.5px] text-ok">
                &gt; queued — this page follows the job as it runs
              </p>
            )}
          </div>
        </Panel>
      )}
    </>
  );
}
